// Types partagés entre le backend et le front.
//
// Ce module est la traduction TypeScript des types ENUM PostgreSQL déclarés
// dans backend/migrations. Toute valeur ajoutée ici doit l'être dans une
// migration, et réciproquement — c'est le seul endroit où les deux mondes se
// contredisent silencieusement si on n'y prend pas garde.

/** shot_outcome — les sept issues de la saisie 1-Tap, dans l'ordre des boutons. */
export const SHOT_OUTCOMES = [
    'center',   // Réussi / centré
    'slice',
    'hook',
    'top',
    'fat',      // Gratte
    'shank',    // Socket
    'miss',     // Raté
] as const;
export type ShotOutcome = (typeof SHOT_OUTCOMES)[number];

/** Libellés français, pour l'affichage des boutons et des graphiques. */
export const SHOT_OUTCOME_LABELS: Record<ShotOutcome, string> = {
    center: 'Réussi',
    slice: 'Slice',
    hook: 'Hook',
    top: 'Top',
    fat: 'Gratte',
    shank: 'Socket',
    miss: 'Raté',
};

/** Seule une issue réussie accepte une distance — cf. la contrainte
 *  shots_distance_only_on_success en base. */
export const acceptsDistance = (outcome: ShotOutcome): boolean => outcome === 'center';

/* ── Où la balle est tombée, sur le parcours ─────────────────────────────────
 *
 * Le practice qualifie le CONTACT — centré, slice, socket — parce que c'est
 * l'objet de la séance. Le parcours note la ZONE, parce que c'est elle qui
 * décide de la suite : on marche jusqu'à sa balle et l'endroit où elle repose
 * commande le club, la pénalité et le score.
 *
 * C'est aussi la seule saisie d'où sortent les trois mesures que tout golfeur
 * connaît — fairway et green en régulation, scrambling. Aucune ne se déduit
 * d'un « slice ».
 *
 * L'ordre de cette liste est celui de l'écran, du meilleur au pire : il sert
 * aussi bien à dessiner le pavé qu'à trier un tableau. */
export const SHOT_LANDINGS = [
    'fairway',
    'green',
    'rough',
    'bunker',
    'penalty',        // Piquets rouges ou jaunes
    'out_of_bounds',  // Piquets blancs, ou balle perdue
] as const;
export type ShotLanding = (typeof SHOT_LANDINGS)[number];

export const SHOT_LANDING_LABELS: Record<ShotLanding, string> = {
    fairway: 'Fairway',
    green: 'Green',
    rough: 'Rough',
    bunker: 'Bunker',
    penalty: 'Eau / pénalité',
    out_of_bounds: 'Hors-limites',
};

/** Ce que la zone a coûté, et pourquoi.
 *
 *  Les trois motifs valent un coup aujourd'hui. Le nombre est stocké à part en
 *  base : la règle en connaît d'autres, et un booléen aurait fermé la porte
 *  pour rien. */
export const SHOT_PENALTIES = ['relief', 'stroke_and_distance', 'unplayable'] as const;
export type ShotPenalty = (typeof SHOT_PENALTIES)[number];

/** La phrase qu'on lit en touchant un coup pénalisé sur le ruban. Elle dit la
 *  RÈGLE, pas seulement le chiffre : « +1 » sans motif est un chiffre qu'on ne
 *  sait plus expliquer trois trous plus loin. */
export const SHOT_PENALTY_LABELS: Record<ShotPenalty, string> = {
    relief: 'Dégagement de la zone à pénalité',
    stroke_and_distance: 'Coup et distance — hors-limites ou balle perdue',
    unplayable: 'Balle injouable',
};

/** Combien de coups chaque motif ajoute. Une fonction et non une table figée
 *  dans l'écran : le serveur valide la même règle. */
export const penaltyStrokes = (reason: ShotPenalty | null): number => (reason == null ? 0 : 1);

/* ── Les pénalités qui ne viennent pas d'un coup ─────────────────────────────
 *
 * Celles d'au-dessus sanctionnent OÙ la balle est allée : elles se rattachent
 * au coup qui l'y a envoyée, et l'écran les propose au moment de le noter.
 * Beaucoup d'autres n'ont aucun rapport avec un coup — toucher le sable en
 * entrant dans un bunker, déplacer sa balle en écartant une feuille, demander
 * un conseil. Elles arrivent ENTRE les coups, et n'avaient nulle part où aller.
 *
 * Elles se posent donc sur le TROU, pas sur un coup : rattacher « j'ai touché
 * le sable » au dernier coup joué en ferait un mauvais coup, et la statistique
 * de zone d'arrivée s'en trouverait faussée. Le trou en porte la liste, et le
 * total du trou les additionne.
 *
 * ── Un coup ou deux ─────────────────────────────────────────────────────────
 *
 * En stroke play la pénalité générale vaut DEUX coups ; quelques infractions
 * n'en valent qu'un. C'est la seule chose que le joueur n'a pas à savoir : il
 * nomme ce qui s'est passé, le barème suit. */
export const HOLE_PENALTIES = [
    // Les deux du TERRAIN d'abord : ce sont les plus fréquentes, et les seules
    // qu'on sait souvent avant même d'avoir marché jusqu'à la balle.
    'hors_limites', 'injouable',
    'sable_touche', 'balle_bougee', 'mauvais_endroit', 'mauvaise_balle',
    'conseil', 'jeu_lent', 'autre_1', 'autre_2',
] as const;
export type HolePenalty = (typeof HOLE_PENALTIES)[number];

/** Ce qui s'est passé, dit comme on le dirait à son partenaire. */
export const HOLE_PENALTY_LABELS: Record<HolePenalty, string> = {
    hors_limites: 'Hors-limites ou balle perdue',
    injouable: 'Balle injouable',
    sable_touche: 'Sable touché dans le bunker',
    balle_bougee: 'Balle déplacée',
    mauvais_endroit: "Balle jouée d'un mauvais endroit",
    mauvaise_balle: 'Mauvaise balle jouée',
    conseil: 'Conseil demandé ou donné',
    jeu_lent: 'Jeu lent',
    autre_1: 'Autre infraction',
    autre_2: 'Autre infraction',
};

/** La règle, citée. « +2 » sans référence est un chiffre qu'on ne sait plus
 *  défendre au moment de rendre la carte — et sur un point de règle, c'est
 *  précisément là que la question se pose. */
export const HOLE_PENALTY_RULES: Record<HolePenalty, string> = {
    hors_limites: 'Règle 18.2 — coup et distance',
    injouable: 'Règle 19 — dégagement au choix du joueur',
    sable_touche: 'Règle 12.2b — toucher le sable avant le coup',
    balle_bougee: 'Règle 9.4b — balle déplacée par le joueur',
    mauvais_endroit: 'Règle 14.7 — jouer d’un mauvais endroit',
    mauvaise_balle: 'Règle 6.3c — jouer une mauvaise balle',
    conseil: 'Règle 10.2a — conseil entre joueurs',
    jeu_lent: 'Règle 5.6b — cadence de jeu, premier manquement',
    autre_1: 'Une pénalité d’un coup, hors de cette liste',
    autre_2: 'La pénalité générale, hors de cette liste',
};

/** Le barème. Comme pour les pénalités de coup, le CLIENT ne transmet jamais
 *  le nombre : le serveur le déduit du motif, sans quoi deux versions de
 *  l'application finiraient par ne plus compter pareil. */
/** Le barème. Un coup pour les pénalités de terrain — hors-limites, injouable,
 *  balle déplacée, jeu lent — deux pour la pénalité générale du stroke play. */
const UN_COUP: readonly HolePenalty[] = [
    'hors_limites', 'injouable', 'balle_bougee', 'jeu_lent', 'autre_1',
];
export const holePenaltyStrokes = (reason: HolePenalty): number =>
    (UN_COUP.includes(reason) ? 1 : 2);

/** Ce que la liste des pénalités d'un trou ajoute à son score. */
export const holePenaltyTotal = (reasons: readonly HolePenalty[] | null | undefined): number =>
    (reasons ?? []).reduce((n, r) => n + holePenaltyStrokes(r), 0);

/** Ce que la zone coûte AVANT que le joueur en dise plus.
 *
 *  Le pavé enregistre d'un seul appui, sur une estimation : la balle est dans
 *  l'eau, elle est hors-limites. La règle qui suit le plus souvent est déjà
 *  connue à cet instant — on se dégage, et hors-limites c'est coup et distance
 *  sans alternative. La poser d'emblée évite de faire commencer chaque balle
 *  par une question, et l'écran de bilan permet de la retirer d'un tap quand
 *  ce n'est pas le cas. */
export const defaultPenalty = (landing: ShotLanding): ShotPenalty | null => {
    if (landing === 'penalty') return 'relief';
    if (landing === 'out_of_bounds') return 'stroke_and_distance';
    return null;
};

/** Les motifs qu'une zone autorise, dans l'ordre où l'écran les propose.
 *
 *  Le hors-limites n'en offre qu'un : la règle 18 est sans alternative, et
 *  proposer de l'enlever laisserait croire qu'on a le choix. Le fairway et le
 *  green n'en offrent aucun — on n'y est pénalisé de rien. */
export const allowedPenalties = (landing: ShotLanding): readonly ShotPenalty[] => {
    if (landing === 'penalty') return ['relief', 'unplayable'];
    if (landing === 'out_of_bounds') return ['stroke_and_distance'];
    if (landing === 'rough' || landing === 'bunker') return ['unplayable'];
    return [];
};

/** club_category */
export const CLUB_CATEGORIES = ['driver', 'wood', 'hybrid', 'iron', 'wedge', 'putter'] as const;
export type ClubCategory = (typeof CLUB_CATEGORIES)[number];

export const CLUB_CATEGORY_LABELS: Record<ClubCategory, string> = {
    driver: 'Driver',
    wood: 'Bois',
    hybrid: 'Hybride',
    iron: 'Fer',
    wedge: 'Wedge',
    putter: 'Putter',
};

/** Les mêmes familles, au pluriel : un intertitre couvre plusieurs clubs.
 *
 *  Une table et non un « s » ajouté à la volée : « Bois » est déjà au pluriel
 *  et « Boiss » serait le résultat de la règle naïve. Six mots écrits une fois
 *  valent mieux qu'une règle qui a déjà une exception sur six cas. */
export const CLUB_CATEGORY_LABELS_PLURAL: Record<ClubCategory, string> = {
    driver: 'Drivers',
    wood: 'Bois',
    hybrid: 'Hybrides',
    iron: 'Fers',
    wedge: 'Wedges',
    putter: 'Putters',
};

/** user_role */
export const USER_ROLES = ['user', 'coach', 'admin'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** session_status */
export const SESSION_STATUSES = ['in_progress', 'completed', 'aborted'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

/** Règle 4.1b : 14 clubs maximum dans un sac. La limite est imposée en base
 *  par un trigger ; cette constante sert à l'afficher avant de tenter l'ajout. */
export const MAX_CLUBS_PER_BAG = 14;

/** training_mode */
export const TRAINING_MODES = ['triple_shot', 'feet_together', 'free'] as const;
export type TrainingModeCode = (typeof TRAINING_MODES)[number];

/** Nombre de balles par série en mode Triple Shot. */
export const SHOTS_PER_SERIES = 3;

/** Combien de balles compose une série, par mode.
 *
 *  `null` : la série est libre et le joueur la ferme lui-même. C'est la
 *  différence de fond entre les deux exercices — le Triple Shot impose un
 *  rythme, le pieds joints laisse taper jusqu'à ce que la sensation revienne. */
export const MODE_SERIES_SIZE: Record<TrainingModeCode, number | null> = {
    triple_shot: SHOTS_PER_SERIES,
    feet_together: null,
    free: null,
};

/** Repère affiché quand la série est libre. Une suggestion, jamais une limite. */
export const MODE_SUGGESTED_SHOTS: Record<TrainingModeCode, number | null> = {
    triple_shot: null,
    feet_together: 10,
    // Aucun repère : c'est le joueur qui définit l'exercice, en suggérer un
    // nombre reviendrait à décider à sa place de ce qu'il est venu faire.
    free: null,
};

/** Borne haute d'une série, imposée en base : au-delà c'est une erreur de
 *  saisie, pas un entraînement. */
export const MAX_SHOTS_PER_SERIES = 50;

export type DominantHand = 'right' | 'left';

/** Les deux barèmes que la fédération distingue. Ce n'est pas une taxonomie
 *  des personnes : c'est la liste des colonnes de son tableau de notation. */
export const GENRES = ['femme', 'homme'] as const;
export type Genre = (typeof GENRES)[number];

export interface SessionUser {
    id: string;
    email: string;
    displayName: string;
    role: UserRole;
    avatarUrl: string | null;
    mustChangePassword: boolean;
    /** Gouverne l'orientation de l'interface de saisie : chez un gaucher, un
     *  slice part à gauche et un hook à droite. Sans cette information, la
     *  moitié des boutons désigneraient la mauvaise direction. */
    dominantHand: DominantHand;
    /** Le genre, et `null` pour n'avoir rien déclaré.
     *
     *  Il ne sert QU'À une chose : choisir laquelle des deux consignes afficher
     *  sur les deux ateliers fédéraux qui ont deux barèmes — cent mètres ou cent
     *  trente à la mise en jeu, soixante ou quatre-vingt-dix à l'attaque de
     *  green. Un carnet de practice n'a pas besoin d'en savoir plus, et toute
     *  autre lecture de ce champ serait un détournement.
     *
     *  `null` n'empêche rien : les deux barèmes s'affichent alors, comme avant
     *  que le réglage existe. */
    gender: Genre | null;
    /** Son index, ou `null` s'il ne l'a jamais saisi. Il voyage avec la session
     *  parce qu'il s'affiche sur son propre avatar, sur tous les ecrans : le
     *  chercher separement ferait une requete de plus a chaque demarrage. */
    handicapIndex: number | null;
}

// Les formules du handicap : coups reçus, coups rendus par trou, score net.
export * from './handicap.ts';
